import { useCallback, useEffect, useState } from "react";
import type { DeviceState } from "@cuekards/core/media-devices";
import { isCaptureKind } from "@cuekards/core/media-devices";
import type { CameraPermission } from "./green-room.js";

/**
 * The hardware, enumerated by the one context that can and resolved by the one
 * that owns the choice.
 *
 * Lifted out of `DeviceSettings` by web-ui ticket 05, because there are now two
 * screens that need it and only one of them is settings. `navigator.mediaDevices`
 * lives where a document does; the selection lives in the core worker with the
 * rest of application state. So this enumerates, reports, and subscribes — and
 * everything either screen draws comes back as a {@link DeviceState} the worker
 * resolved.
 *
 * **The green room needs it for a reason the settings screen never had.** A take
 * borrows the camera the room already has open, and the engine allows that only
 * while the two constraint objects deep-equal — both built by `captureConstraints`
 * over this state. A room that never enumerated would preview on `{}` while the
 * worker, which had, opened the take on a pinned device: no error, and the camera
 * closing and reopening at the end of the countdown. It is also where the camera's
 * name under the stage comes from, and how "No camera found" is told apart from
 * "not asked yet".
 *
 * Hot-plug falls out of it: `devicechange` is another report, so a camera arriving
 * or leaving needs no reload.
 */
export interface Devices {
  /** What the worker resolved, or null before the first answer. */
  state: DeviceState | null;
  /**
   * Whether the devices came back unnamed, which is a browser's way of saying
   * this origin has not been granted media permission. The list is still usable —
   * the core numbers what it cannot name — but "Microphone 2" is not how anybody
   * chooses a microphone.
   */
  needsPermission: boolean;
  /**
   * How many cameras the browser admits exist, or null before the first
   * enumeration.
   *
   * `DeviceState.cameras`, which the worker counts off the raw report before
   * `normaliseDevices` has dropped everything it could not name — the only
   * reading that means anything before permission, and the same number the take's
   * own constraints are decided from, so the room and the recorder cannot
   * disagree about whether there is a camera.
   */
  cameras: number | null;
  /** Enumerate again by hand, after something that might have changed the answer
   * — a permission being granted is the case that matters, because that is when
   * the labels arrive. */
  enumerate: () => Promise<void>;
}

export function useDevices(): Devices {
  const [state, setState] = useState<DeviceState | null>(null);
  const [needsPermission, setNeedsPermission] = useState(false);

  const enumerate = useCallback(async (): Promise<void> => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    window.cuekards.reportDevices(devices.map(toRaw));
    setNeedsPermission(
      devices.some((device) => isCaptureKind(device.kind) && !device.label),
    );
  }, []);

  useEffect(() => {
    const media = navigator.mediaDevices;
    const onDeviceChange = (): void => void enumerate();
    void enumerate();
    media.addEventListener("devicechange", onDeviceChange);
    return () => media.removeEventListener("devicechange", onDeviceChange);
  }, [enumerate]);

  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getDeviceState().then((next) => {
      if (!cancelled) setState(next);
    });
    const off = window.cuekards.onDeviceStateChanged(setState);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  return {
    state,
    needsPermission,
    cameras: state === null || !state.enumerated ? null : state.cameras,
    enumerate,
  };
}

function toRaw(device: MediaDeviceInfo): {
  deviceId: string;
  kind: string;
  label: string;
} {
  return { deviceId: device.deviceId, kind: device.kind, label: device.label };
}

/**
 * What the browser will say about the camera, without opening one (web-ui ticket
 * 05).
 *
 * The green room has to tell "you have never been asked" from "you said no", and
 * nothing in the capture path can: the engine's failure message is shared with
 * the desktop and merges the two under a Windows settings path. The Permissions
 * API answers it directly, and — the part that matters — it answers it *before*
 * anything is opened, which is what lets a first visit draw the gate instead of
 * asking cold.
 *
 * Followed rather than read once. `onchange` fires when the site setting is
 * changed from the address bar, which is precisely the flow the blocked screen
 * tells somebody to use, so the room comes back to life behind them.
 *
 * Every part of it is optional in principle — the API is absent in some engines,
 * the `camera` descriptor is not in the DOM type union, and a browser may reject
 * a name it does not know — so a refusal to answer is `"unknown"`, which the room
 * treats as `"prompt"`: the safe wrong answer, one press from being right.
 */
export function useCameraPermission(): CameraPermission {
  const [permission, setPermission] = useState<CameraPermission>("unknown");

  useEffect(() => {
    let cancelled = false;
    let status: PermissionStatus | null = null;
    const onChange = (): void => {
      if (!cancelled && status !== null) setPermission(status.state);
    };

    void (async () => {
      try {
        const query = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        if (cancelled) return;
        status = query;
        setPermission(query.state);
        query.addEventListener("change", onChange);
      } catch {
        if (!cancelled) setPermission("unknown");
      }
    })();

    return () => {
      cancelled = true;
      status?.removeEventListener("change", onChange);
    };
  }, []);

  return permission;
}
